import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useRealtime } from "@/hooks/useRealtime";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/crm/Badges";
import { AppointmentDialog } from "@/components/crm/appointments/AppointmentDialog";

export const STATUS_TONE = {
  Agendada: "bg-sky-50 text-sky-800 border-sky-200", Confirmada: "bg-teal-50 text-teal-800 border-teal-200",
  Realizada: "bg-emerald-50 text-emerald-800 border-emerald-200", Cancelada: "bg-slate-100 text-slate-500 border-slate-200 line-through",
  "No asistió": "bg-rose-50 text-rose-700 border-rose-200",
};
const VIEWS = [["dia", "Día"], ["semana", "Semana"], ["mes", "Mes"]];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => addDays(d, -((d.getDay() + 6) % 7));

const range = (view, cursor) => {
  if (view === "dia") return [cursor, cursor];
  if (view === "semana") { const s = startOfWeek(cursor); return [s, addDays(s, 6)]; }
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  return [startOfWeek(first), addDays(startOfWeek(last), 6)];
};

const Chip = ({ a, onOpen }) => (
  <button onClick={() => onOpen(a)} data-testid={`appointment-chip-${a.id}`}
          className={`block w-full truncate rounded-lg border px-2 py-1 text-left text-xs transition-transform hover:-translate-y-px ${STATUS_TONE[a.status]}`}>
    <b>{a.time}</b> {a.lead_name} <span className="opacity-70">· {a.service_name || "—"}</span>
  </button>
);

export default function AppointmentsPage() {
  const [params] = useSearchParams();
  const [view, setView] = useState("semana");
  const [cursor, setCursor] = useState(new Date());
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [from, to] = useMemo(() => range(view, cursor), [view, cursor]);

  const load = useCallback(() => {
    api.get("/appointments", { params: { date_from: iso(from), date_to: iso(to), tz_offset: new Date().getTimezoneOffset() } }).then((r) => setItems(r.data));
  }, [from, to]);
  useEffect(() => { load(); }, [load]);
  useRealtime((ev) => { if (ev.type === "notification" || ev.type === "lead_reassigned") load(); });
  useEffect(() => {
    const id = params.get("id");
    if (id) api.get(`/appointments/${id}`).then((r) => setEditing(r.data)).catch(() => {});
  }, [params]);

  const step = (dir) => setCursor((c) => (view === "mes" ? new Date(c.getFullYear(), c.getMonth() + dir, 1) : addDays(c, dir * (view === "dia" ? 1 : 7))));
  const days = [];
  for (let d = new Date(from); d <= to; d = addDays(d, 1)) days.push(new Date(d));
  const byDay = (d) => items.filter((a) => a.date === iso(d));
  const title = view === "mes" ? cursor.toLocaleDateString("es-EC", { month: "long", year: "numeric" })
    : view === "dia" ? cursor.toLocaleDateString("es-EC", { weekday: "long", day: "numeric", month: "long" })
      : `${from.toLocaleDateString("es-EC", { day: "numeric", month: "short" })} – ${to.toLocaleDateString("es-EC", { day: "numeric", month: "short", year: "numeric" })}`;

  return (
    <div data-testid="appointments-page">
      <PageHeader title="Citas" subtitle="Agenda de citas del centro">
        <Button onClick={() => setCreating(true)} className="rounded-xl bg-[#1E6FD9]" data-testid="appointment-new-button"><Plus className="mr-1.5 h-4 w-4" /> Nueva cita</Button>
      </PageHeader>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1" data-testid="appointments-view">
          {VIEWS.map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`appointments-view-${k}`}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${view === k ? "bg-[#0B2545] text-white" : "text-slate-500 hover:text-[#0B2545]"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-xl" onClick={() => step(-1)} data-testid="appointments-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" className="h-9 rounded-xl" onClick={() => setCursor(new Date())} data-testid="appointments-today">Hoy</Button>
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-xl" onClick={() => step(1)} data-testid="appointments-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <p className="font-display text-lg font-bold capitalize" data-testid="appointments-title">{title}</p>
        <span className="text-sm text-slate-500" data-testid="appointments-count">{items.length} citas</span>
      </div>
      {view === "dia" ? (
        <div className="space-y-2 rounded-2xl border border-slate-200 bg-white p-4" data-testid="appointments-day-list">
          {byDay(cursor).length === 0 && <p className="py-10 text-center text-sm text-slate-400">Sin citas este día</p>}
          {byDay(cursor).map((a) => <Chip key={a.id} a={a} onOpen={setEditing} />)}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="grid min-w-[760px] grid-cols-7 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200" data-testid={`appointments-grid-${view}`}>
            {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => <div key={d} className="bg-[#F5F7FA] px-2 py-1.5 text-xs font-semibold text-slate-500">{d}</div>)}
            {days.map((d) => {
              const muted = view === "mes" && d.getMonth() !== cursor.getMonth();
              const today = iso(d) === iso(new Date());
              return (
                <div key={iso(d)} className={`space-y-1 bg-white p-1.5 ${view === "semana" ? "min-h-[260px]" : "min-h-[110px]"} ${muted ? "opacity-50" : ""}`} data-testid={`appointments-day-${iso(d)}`}>
                  <p className={`text-xs font-semibold ${today ? "text-[#1E6FD9]" : "text-slate-500"}`}>{d.getDate()}</p>
                  {byDay(d).map((a) => <Chip key={a.id} a={a} onOpen={setEditing} />)}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <AppointmentDialog open={Boolean(editing)} onOpenChange={(v) => !v && setEditing(null)} appointment={editing} onSaved={load} />
      <AppointmentDialog open={creating} onOpenChange={setCreating} onSaved={load} />
    </div>
  );
}
